import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styles } from './app-profile-features-styles.js';
import { styles as sharedStyles } from '../../styles/shared-styles';
import '@shoelace-style/shoelace/dist/components/card/card.js';
import '@shoelace-style/shoelace/dist/components/tag/tag.js';
import '@shoelace-style/shoelace/dist/components/badge/badge.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import { write } from '../../language/lang';
import { UserProfile } from '../../datatypes/user-profile.js';
import {
  LoginDomainScopeGrant,
  LoginSecurityRoleGrant,
} from '../../api/agwa-api-model';
import { UserAccessManager } from '../../utility/user-access-manager.js';
import { CommonRoleId, DomainId } from '../../utility/claim-id.js';
import { terms } from '../../language/terms';
import { isRetiredSecurityModule } from '../../utility/security/retired-security-modules';

// Interface for our intermediary UI data type
interface FeatureDisplayInfo {
  key: string;
  name: string;
  description?: string;
  isAvailable: boolean;
  displayValue: string;
  roles: RoleDisplayInfo[];
}

interface RoleDisplayInfo {
  code: string;
  label: string;
  description?: string;
}

@customElement('app-profile-features')
export class AppProfileFeatures extends LitElement {
  static styles = [sharedStyles, styles];

  @property({ type: Object })
  userProfile: UserProfile | undefined;

  @state() private domainGrants: LoginDomainScopeGrant[] = [];

  private get userAccess(): UserAccessManager | undefined {
    return this.userProfile?.access;
  }

  connectedCallback() {
    super.connectedCallback();
    this.loadSecurityContext();
  }

  updated(changedProps: Map<string, unknown>) {
    if (changedProps.has('userProfile')) {
      this.loadSecurityContext();
    }
  }

  private loadSecurityContext() {
    this.domainGrants = this.userAccess?.getDomainGrants() ?? [];
  }

  isAdmin(): boolean {
    return (
      this.userAccess !== undefined &&
      this.userAccess.hasRole(CommonRoleId.admin) &&
      this.userAccess.hasDomain(DomainId.systemTenant)
    );
  }

  private buildSecurityModuleFeatures(): FeatureDisplayInfo[] {
    // A retired module's grants are not shown as a feature (D-175). The person still holds them.
    const assignments = (this.userAccess?.getRoleAssignments() ?? []).filter(
      (assignment) => !isRetiredSecurityModule(assignment.moduleCode)
    );
    if (!assignments.length) return [];

    const grouped = new Map<string, FeatureDisplayInfo>();
    for (const assignment of assignments) {
      const moduleCode =
        assignment.moduleCode || assignment.moduleId || assignment.code;
      const key = `module:${moduleCode.toLowerCase()}`;
      const moduleName = assignment.moduleDisplayName || moduleCode;
      if (!grouped.has(key)) {
        grouped.set(key, {
          key,
          name: moduleName,
          description: `Access provided by ${moduleName}.`,
          isAvailable: true,
          displayValue: moduleName,
          roles: [],
        });
      }
      const bucket = grouped.get(key)!;
      const roleDisplay = this.describeSecurityRole(assignment);
      if (!bucket.roles.some((role) => role.code === roleDisplay.code)) {
        bucket.roles.push(roleDisplay);
      }
    }

    return Array.from(grouped.values())
      .map((feature) => ({
        ...feature,
        roles: feature.roles.sort((a, b) => a.label.localeCompare(b.label)),
      }))
      .sort((a, b) => a.displayValue.localeCompare(b.displayValue));
  }

  private describeSecurityRole(
    assignment: LoginSecurityRoleGrant
  ): RoleDisplayInfo {
    const rawCode = assignment.code.startsWith('role.')
      ? assignment.code
      : `role.${assignment.code}`;
    const label = assignment.displayName || this.formatRoleCode(rawCode);

    return {
      code: rawCode,
      label,
    };
  }

  private formatRoleCode(roleKey: string): string {
    const trimmed = roleKey.replace(/^role\./i, '');
    return trimmed
      .split('.')
      .filter(Boolean)
      .map((segment) =>
        segment
          .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
          .replace(/[-_]/g, ' ')
          .trim()
      )
      .join(' ');
  }

  render() {
    if (!this.userProfile) {
      return html`<p>Loading ${terms.loginUser.lower} features...</p>`;
    }

    const featuresDisplayData = this.buildSecurityModuleFeatures();
    const domainChips = this.domainGrants;

    return html`
      <div class="features-container">
        <sl-card class="features-card">
          <div slot="header" class="card-header">
            <sl-icon name="gear-wide-connected"></sl-icon>
            <h3>${write`Available Features`}</h3>
          </div>

          ${
            domainChips.length
              ? html`
                  <div class="domain-section">
                    <div class="domain-label">
                      ${terms.userGroupDomain.plural}
                    </div>
                    <div class="domain-chips">
                      ${domainChips.map(
                        (domain) =>
                          html`<sl-tag pill
                            >${
                              domain.domainDisplayName ?? domain.domainCode
                            }</sl-tag
                          >`
                      )}
                    </div>
                  </div>
                `
              : null
          }

          <div class="features-list">
            ${
              featuresDisplayData.length > 0
                ? featuresDisplayData.map((feature) =>
                    this.renderFeature(feature)
                  )
                : html`<p class="empty-message">
                    ${write`No features available`}
                  </p>`
            }
          </div>
        </sl-card>
      </div>
    `;
  }

  private renderFeature(feature: FeatureDisplayInfo) {
    const iconName = feature.isAvailable ? 'check-circle-fill' : 'x-circle';
    const badgeVariant = feature.isAvailable ? 'success' : 'neutral';
    const badgeLabel = feature.isAvailable
      ? write`Available`
      : write`Not Available`;
    return html`
      <div class="feature-item">
        <div class="feature-name">
          <sl-icon
            name="${iconName}"
            class="feature-icon ${
              feature.isAvailable ? 'available' : 'unavailable'
            }"
          ></sl-icon>
          <div class="feature-name-text">
            <span class="feature-title">${feature.displayValue}</span>
            ${
              feature.description
                ? html`<span class="feature-description"
                    >${feature.description}</span
                  >`
                : null
            }
          </div>
        </div>
        <sl-badge variant="${badgeVariant}">${badgeLabel}</sl-badge>
        ${
          feature.roles.length
            ? html`
                <div class="feature-role-chip-container">
                  ${feature.roles.map((role) => this.renderRoleTag(role))}
                </div>
              `
            : null
        }
      </div>
    `;
  }

  private renderRoleTag(role: RoleDisplayInfo) {
    const title = role.description ?? role.label;
    return html`<sl-tag variant="primary" size="large" pill title="${title}">
      ${role.label}
    </sl-tag>`;
  }
}
